import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
	experimentalLocationGetExperimentalLocations,
	fieldNotesCreateFieldNote,
	fieldNotesDeleteFieldNote,
	fieldNotesGetFieldNotes,
} from '../../api';
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto as ExperimentalLocationDto,
	SmartField_Api_Contracts_FieldNotes_FieldNoteLocationDto as FieldNoteLocationDto,
} from '../../api';
import { Modal } from '../../components/ui/Modal';
import { LocationPickerList, type LocationGroup } from '../../components/ui/LocationPicker';
import { SmartFieldClaimType, useAuth, useHasClaimForSite, useIsSiteManagerForSite } from '../../lib/auth';

const MAX_NOTE_LENGTH = 10_000;

type Draft = { experimentalLocationIds: number[]; text: string };

const STATE_ORDER = ['Field', 'Block', 'MiniBlock', 'Plot'] as const;

const locationLabel = (el: ExperimentalLocationDto) =>
	el.friendlyName || `${el.state} ${el.plotId ?? el.miniBlockId ?? el.blockId ?? el.fieldId ?? el.id}`;

function draftIds(draft: Record<string, unknown>): number[] {
	if (Array.isArray(draft.experimentalLocationIds)) {
		return draft.experimentalLocationIds.filter((id): id is number => typeof id === 'number');
	}
	// Drafts saved by the single-location version stored one id.
	if (typeof draft.experimentalLocationId === 'number') {
		return [draft.experimentalLocationId];
	}
	return [];
}

function loadDraft(key: string): Draft | null {
	try {
		const raw = localStorage.getItem(key);
		const draft = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
		if (typeof draft?.text !== 'string' || !draft.text) {
			return null;
		}
		return { experimentalLocationIds: draftIds(draft), text: draft.text };
	} catch {
		return null;
	}
}

function saveDraft(key: string, draft: Draft) {
	try {
		if (draft.text) {
			localStorage.setItem(key, JSON.stringify(draft));
		} else {
			localStorage.removeItem(key);
		}
	} catch {
		// Storage full or disabled (private mode) — the note still lives in component state.
	}
}

// Prefer the site's location list for the badge text: it knows plot/block numbers even when
// the note only carries a bare id (e.g. locations without a friendly name).
function badgeLabel(location: FieldNoteLocationDto, locationsById: Map<number, ExperimentalLocationDto>): string {
	if (location.state === 'Site') {
		return 'Site';
	}
	const el = location.id != null ? locationsById.get(location.id) : undefined;
	const name = el?.friendlyName
		?? location.friendlyName
		?? (el ? `${el.plotId ?? el.miniBlockId ?? el.blockId ?? el.fieldId ?? el.id}` : `#${location.id}`);
	return `${location.state} · ${name}`;
}

export function FieldNotesPage() {
	const { siteId: siteIdParam } = useParams<{ siteId: string }>();
	const siteId = parseInt(siteIdParam ?? '', 10);
	const queryClient = useQueryClient();
	const isSiteManager = useIsSiteManagerForSite(siteId);
	const isRawDataEditor = useHasClaimForSite(SmartFieldClaimType.RawDataEditor, siteId);
	const canWrite = isSiteManager || isRawDataEditor;
	const { user } = useAuth();
	// Per user, so a shared browser never restores someone else's unsent text under a new login.
	const draftKey = `field-note-draft-${user?.email ?? ''}-${siteId}`;

	const [locationFilter, setLocationFilter] = useState<number | null>(null);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const hasDraft = !isFormOpen && loadDraft(draftKey) !== null;

	const { data: notes, isLoading, isError, refetch } = useQuery({
		queryKey: ['field-notes', siteId, locationFilter],
		queryFn: () => fieldNotesGetFieldNotes({ siteId, experimentalLocationId: locationFilter ?? undefined }),
	});

	const { data: experimentalLocations } = useQuery({
		queryKey: ['experimental-locations', siteId],
		queryFn: () => experimentalLocationGetExperimentalLocations({ siteId }),
	});

	const locationGroups = useMemo(
		() =>
			STATE_ORDER.map((state) => ({
				state,
				options: (experimentalLocations ?? [])
					.filter((el) => el.state === state && el.id != null)
					.map((el) => ({ id: el.id!, label: locationLabel(el) }))
					.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
			})).filter((g) => g.options.length > 0),
		[experimentalLocations]
	);

	const locationsById = useMemo(
		() => new Map((experimentalLocations ?? []).filter((el) => el.id != null).map((el) => [el.id!, el])),
		[experimentalLocations]
	);

	const deleteMutation = useMutation({
		mutationFn: (id: number) => fieldNotesDeleteFieldNote({ siteId, id }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['field-notes', siteId] });
			toast.success('Note deleted');
		},
		onError: (error) => {
			toast.error(`Failed to delete note: ${error.message}`);
		},
	});

	return (
		<div className="space-y-4 max-w-3xl">
			<div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
				<LocationFilterSelect groups={locationGroups} selectedId={locationFilter} onSelect={setLocationFilter} />
				{canWrite && (
					<button
						type="button"
						onClick={() => setIsFormOpen(true)}
						className="px-4 py-3 sm:py-2 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium"
					>
						{hasDraft ? 'Continue draft' : '+ New note'}
					</button>
				)}
			</div>

			{isLoading ? (
				<div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading notes…</div>
			) : isError ? (
				<div className="bg-white rounded-lg shadow p-8 text-center text-red-600 space-y-3">
					<p>Could not load notes.</p>
					<button type="button" onClick={() => refetch()} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">
						Retry
					</button>
				</div>
			) : notes && notes.length > 0 ? (
				<ul className="space-y-3">
					{notes.map((note) => (
						<li key={note.id} className="bg-white rounded-lg shadow p-4">
							<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-gray-500 mb-2">
								<span className="font-medium text-gray-900">{note.authorEmail ?? 'Unknown author'}</span>
								<span>{note.createdAtUtc ? new Date(note.createdAtUtc).toLocaleString() : ''}</span>
							{(note.locations ?? []).map((location) => (
								<span key={location.id} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
									{badgeLabel(location, locationsById)}
								</span>
							))}
								{note.canDelete && (
									<button
										type="button"
										disabled={deleteMutation.isPending}
										onClick={() => {
											if (window.confirm('Delete this note?')) {
												deleteMutation.mutate(note.id!);
											}
										}}
										className="ml-auto px-2 py-1 text-red-600 rounded disabled:opacity-50"
									>
										Delete
									</button>
								)}
							</div>
							<p className="whitespace-pre-wrap break-words text-gray-900">{note.text}</p>
						</li>
					))}
				</ul>
			) : (
				<div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No notes yet</div>
			)}

			{isFormOpen && (
				<FieldNoteFormModal
					key={draftKey}
					siteId={siteId}
					draftKey={draftKey}
					defaultLocationIds={locationFilter == null ? [] : [locationFilter]}
					locationGroups={locationGroups}
					onClose={() => setIsFormOpen(false)}
				/>
			)}
		</div>
	);
}

function LocationFilterSelect({
	groups,
	selectedId,
	onSelect,
}: {
	groups: LocationGroup[];
	selectedId: number | null;
	onSelect: (id: number | null) => void;
}) {
	const [open, setOpen] = useState(false);
	const selectedLabel = useMemo(() => {
		if (selectedId == null) {
			return 'All notes';
		}
		return groups.flatMap((g) => g.options).find((o) => o.id === selectedId)?.label ?? 'All notes';
	}, [groups, selectedId]);

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-base sm:text-sm"
				aria-label="Filter notes by location"
			>
				{selectedLabel}
				<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="w-4 h-4 text-gray-500">
					<path
						fillRule="evenodd"
						d="M5.227 7.28a.75.75 0 0 1 1.06 0L10 10.993l3.713-3.713a.75.75 0 1 1 1.06 1.06l-4.243 4.243a.75.75 0 0 1-1.06 0L5.227 8.34a.75.75 0 0 1 0-1.06Z"
						clipRule="evenodd"
					/>
				</svg>
			</button>
			{open &&
				createPortal(
					<Modal open onClose={() => setOpen(false)} title="Filter notes" sheet>
						<div className="p-4 sm:p-6">
							<h2 className="text-xl font-bold text-gray-900 mb-3">Filter notes</h2>
							<LocationPickerList
								single
								autoFocusSearch
								groups={groups}
								selectedIds={selectedId == null ? [] : [selectedId]}
								noneLabel="All notes"
								onChange={(ids) => onSelect(ids.length > 0 ? ids[ids.length - 1]! : null)}
								onDone={() => setOpen(false)}
							/>
						</div>
					</Modal>,
					document.body,
				)}
		</>
	);
}

type FieldNoteFormModalProps = {
	siteId: number;
	draftKey: string;
	defaultLocationIds: number[];
	locationGroups: LocationGroup[];
	onClose: () => void;
};

function FieldNoteFormModal({ siteId, draftKey, defaultLocationIds, locationGroups, onClose }: FieldNoteFormModalProps) {
	const queryClient = useQueryClient();
	// isPending only flips on the next render, so a fast double-click would otherwise submit twice.
	const submittingRef = useRef(false);
	const [restoredDraft] = useState(() => loadDraft(draftKey));
	const [draft, setDraft] = useState<Draft>(restoredDraft ?? { experimentalLocationIds: defaultLocationIds, text: '' });

	const updateDraft = (next: Draft) => {
		setDraft(next);
		// Saved on every keystroke rather than debounced: cheap, and nothing typed is lost on reload.
		saveDraft(draftKey, next);
	};

	const createMutation = useMutation({
		mutationFn: (d: Draft) =>
			fieldNotesCreateFieldNote({ siteId, requestBody: { experimentalLocationIds: d.experimentalLocationIds, text: d.text } }),
		onSuccess: () => {
			saveDraft(draftKey, { experimentalLocationIds: [], text: '' });
			queryClient.invalidateQueries({ queryKey: ['field-notes', siteId] });
			toast.success('Note saved');
			onClose();
		},
		onError: (error) => {
			toast.error(`Failed to save note: ${error.message}`);
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (draft.text.trim() && !submittingRef.current) {
			submittingRef.current = true;
			createMutation.mutate(draft, { onSettled: () => { submittingRef.current = false; } });
		}
	};

	// Escape and backdrop clicks must not close the form mid-save; the draft is kept either way.
	const requestClose = () => {
		if (!createMutation.isPending) {
			onClose();
		}
	};

	return createPortal(
		<Modal open onClose={requestClose} title="New field note" sheet>
			<form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3">
				<h2 className="text-xl font-bold text-gray-900">New field note</h2>

				{restoredDraft && (
					<p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
						Restored unsaved draft.
					</p>
				)}

				<LocationPickerList
					groups={locationGroups}
					selectedIds={draft.experimentalLocationIds}
					noneLabel="Whole site"
					disabled={createMutation.isPending}
					onChange={(ids) => updateDraft({ ...draft, experimentalLocationIds: ids })}
				/>

				<textarea
					value={draft.text}
					onChange={(e) => updateDraft({ ...draft, text: e.target.value })}
					readOnly={createMutation.isPending}
					maxLength={MAX_NOTE_LENGTH}
					rows={6}
					data-autofocus
					placeholder="What happened, and why?"
					className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 text-base focus:ring-2 focus:ring-smartfield focus:border-transparent"
				/>
				<p className="text-xs text-gray-500 text-right">{draft.text.length} / {MAX_NOTE_LENGTH}</p>

				<div className="flex gap-3">
					<button
						type="button"
						onClick={onClose}
						disabled={createMutation.isPending}
						className="flex-1 sm:flex-none px-4 py-3 sm:py-2 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50"
					>
						Close
					</button>
					<button
						type="submit"
						disabled={createMutation.isPending || !draft.text.trim()}
						className="flex-1 sm:flex-none px-4 py-3 sm:py-2 bg-smartfield text-white rounded-lg font-medium disabled:opacity-50"
					>
						{createMutation.isPending ? 'Saving…' : 'Save note'}
					</button>
				</div>
			</form>
		</Modal>,
		document.body
	);
}
